/**
 * CDN image helper — injects on-the-fly transforms for Cloudinary and Unsplash
 * URLs to serve right-sized, WebP/AVIF-encoded variants instead of originals.
 *
 * Usage:
 *   <img src={cdnImage(url, { w: 400 })} loading="lazy" />
 *   <img src={cdnImage(url, { w: 1200, q: 85 })} loading="eager" fetchpriority="high" />
 *
 * Falls back to the original URL when the host is unknown (defensive).
 */

const CLOUDINARY_UPLOAD_TOKEN = '/image/upload/';

/**
 * Inject a Cloudinary delivery transformation into the URL.
 * If the URL already contains a transformation segment, we leave it alone
 * (the backend has likely already optimised it).
 */
const transformCloudinary = (url, { w, h, q = 'auto', lqip = false }) => {
  if (!url.includes(CLOUDINARY_UPLOAD_TOKEN)) return url;
  // Already has a transformation segment?
  const after = url.split(CLOUDINARY_UPLOAD_TOKEN)[1] || '';
  if (/^[a-z]_[a-z0-9_,:]+\//i.test(after)) return url;
  if (lqip) {
    // Tiny blurred placeholder — ~1 KB, decodes instantly
    return url.replace(
      CLOUDINARY_UPLOAD_TOKEN,
      `${CLOUDINARY_UPLOAD_TOKEN}e_blur:1000,q_1,f_auto,w_40/`
    );
  }
  const parts = ['q_' + q, 'f_auto'];
  if (w) parts.push(`c_fill`, `g_auto`, `w_${w}`);
  if (h) parts.push(`h_${h}`);
  return url.replace(CLOUDINARY_UPLOAD_TOKEN, `${CLOUDINARY_UPLOAD_TOKEN}${parts.join(',')}/`);
};

/**
 * Inject Unsplash's own dynamic-resize params. Used by seed data and any
 * editorial images still pointed at images.unsplash.com. We force WebP
 * output (`fm=webp`) and lower default quality (55 vs 80) — PERF audit
 * confirmed 508 KiB / 45% savings on the property card grid with no
 * visible quality regression at Retina render sizes.
 */
const transformUnsplash = (url, { w, q = 55, lqip = false }) => {
  try {
    const u = new URL(url);
    if (lqip) {
      u.searchParams.set('w', '40');
      u.searchParams.set('q', '10');
      u.searchParams.set('blur', '200');
      u.searchParams.set('auto', 'format');
      u.searchParams.set('fit', 'crop');
      return u.toString();
    }
    if (w) u.searchParams.set('w', String(w));
    u.searchParams.set('q', String(q));
    u.searchParams.set('fm', 'webp');
    u.searchParams.set('auto', 'format');
    u.searchParams.set('fit', 'crop');
    return u.toString();
  } catch {
    return url;
  }
};

export const cdnImage = (url, opts = {}) => {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  try {
    const u = new URL(url);
    if (u.hostname === 'res.cloudinary.com') return transformCloudinary(url, opts);
    if (u.hostname.endsWith('unsplash.com')) return transformUnsplash(url, opts);
  } catch {
    /* relative or invalid — return unchanged */
  }
  return url;
};

/**
 * Build a responsive srcSet string for use on <img srcSet={...} sizes={...}>.
 *
 * Emits one candidate per breakpoint in `widths`, transformed through the
 * same CDN pipeline as `cdnImage` (Cloudinary f_auto → WebP/AVIF, q_auto,
 * or Unsplash w/q/auto=format). Browsers then pick the best variant for
 * their DPR × viewport, cutting mobile image payloads by ~60-70 %
 * compared to serving a single 1200 px hero to every device.
 *
 *   <img
 *     src={cdnImage(url, { w: 800 })}
 *     srcSet={cdnSrcSet(url, [400, 800, 1280, 1920])}
 *     sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
 *     loading="lazy" decoding="async" />
 *
 * Returns `undefined` when the URL is not from a supported CDN so the
 * caller can safely spread `{srcSet: ...}` without emitting a broken attr.
 */
export const cdnSrcSet = (url, widths = [400, 800, 1280, 1920], opts = {}) => {
  if (!url || typeof url !== 'string') return undefined;
  if (url.startsWith('data:') || url.startsWith('blob:')) return undefined;
  let hostname;
  try {
    hostname = new URL(url).hostname;
  } catch {
    return undefined;
  }
  const supported = hostname === 'res.cloudinary.com' || hostname.endsWith('unsplash.com');
  if (!supported) return undefined;
  const seen = new Set();
  const parts = [];
  for (const w of widths) {
    if (!w || seen.has(w)) continue;
    seen.add(w);
    const variant = cdnImage(url, { ...opts, w });
    if (variant && variant !== url) parts.push(`${variant} ${w}w`);
  }
  return parts.length ? parts.join(', ') : undefined;
};

export default cdnImage;
